import { ReactNode, useCallback, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Plus, Trash2 } from 'lucide-react'

import Card from 'app/frontend/base/components/Card'
import Main from 'app/frontend/base/components/Main'
import Select from 'app/frontend/base/components/Select'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  DevAssignmentEncounter,
  DevAssignmentInstance,
  DevRaidPlanAssignmentDefaults,
  getDevEncounterCustomAbilities,
  getDevRaidPlanAssignmentDefaults,
  getDevRaidPlanAssignmentInstances,
  saveDevEncounterCustomAbilities,
  saveDevRaidPlanAssignmentDefaults,
} from 'app/frontend/base/api/devRaidPlanAssignmentDefaults'
import { RaidPlanOwner } from 'app/frontend/base/api/raid_plans'
import { GameVersion } from 'app/frontend/types/custom/CharacterClass'
import RaidPlan, {
  ConfiguredCustomAbility,
  RaidPlanTimeline,
} from 'app/frontend/types/custom/RaidPlan'
import useTeamPlanComputedData from 'app/frontend/base/pages/TeamDashboard/subpages/TeamPlans/hooks/useTeamPlanComputedData'
import TeamPlanAssignments from 'app/frontend/base/pages/TeamDashboard/subpages/TeamPlans/components/TeamPlanAssignments'
import { TeamPlanTimelineProvider } from 'app/frontend/base/pages/TeamDashboard/subpages/TeamPlans/components/TeamPlanTimelineContext'
import SpellIcon from 'app/frontend/base/pages/TeamDashboard/subpages/TeamPlans/components/SpellIcon'

const DEFAULT_CUSTOM_ABILITY_COLOR = 'rgb(255, 255, 255)'

const rgbToHex = (rgb: string) => {
  const match = rgb.match(/rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/)
  if (!match) return '#ffffff'
  const toHex = (value: string) => Number(value).toString(16).padStart(2, '0')
  return `#${toHex(match[1] ?? '255')}${toHex(match[2] ?? '255')}${toHex(
    match[3] ?? '255',
  )}`
}

const hexToRgb = (hex: string) => {
  const match = hex.match(/^#?([0-9a-f]{6})$/i)
  if (!match) return DEFAULT_CUSTOM_ABILITY_COLOR
  const value = parseInt(match[1] ?? 'ffffff', 16)
  return `rgb(${(value >> 16) & 255}, ${(value >> 8) & 255}, ${value & 255})`
}

const DEV_OWNER: RaidPlanOwner = { ownerType: 'user' }

const cloneTimeline = (timeline: RaidPlanTimeline): RaidPlanTimeline =>
  JSON.parse(JSON.stringify(timeline)) as RaidPlanTimeline

const assignmentsFromTimeline = (
  timeline: RaidPlanTimeline,
): DevRaidPlanAssignmentDefaults => ({
  abilities: timeline.encounter_assignments?.abilities ?? {},
  ability_order: timeline.encounter_assignments?.ability_order ?? [],
})

const timelineFromAssignments = (
  assignments: DevRaidPlanAssignmentDefaults,
): RaidPlanTimeline => ({
  actors: [],
  encounter_assignments: {
    abilities: assignments.abilities ?? {},
    ability_order: assignments.ability_order ?? [],
  },
})

const buildFakeRaidPlan = (
  encounterId: number,
  timeline: RaidPlanTimeline,
): RaidPlan => ({
  id: 0,
  ownerId: 0,
  ownerType: 'User',
  encounterId,
  difficulty: 'mythic',
  name: 'Assignment defaults',
  timeline,
  createdAt: '',
  updatedAt: '',
})

const RaidPlanAssignmentDefaultsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const instanceId = searchParams.get('instance') ?? undefined
  const encounterId = searchParams.get('encounter') ?? undefined

  const { data, isLoading, error } = useQuery({
    queryKey: ['devRaidPlanAssignmentInstances'],
    queryFn: getDevRaidPlanAssignmentInstances,
  })

  const instances = data?.instances ?? []

  const selectedInstance = useMemo(
    () => instances.find((instance) => String(instance.id) === instanceId),
    [instances, instanceId],
  )

  const encounters = selectedInstance?.encounters ?? []

  const selectedEncounter = useMemo(
    () => encounters.find((encounter) => String(encounter.id) === encounterId),
    [encounters, encounterId],
  )

  const instanceOptions = instances.map((instance: DevAssignmentInstance) => ({
    value: String(instance.id),
    label: `${instance.name} (${instance.gameVersion})`,
    sortLabel: '',
  }))

  const encounterOptions = encounters.map(
    (encounter: DevAssignmentEncounter) => ({
      value: String(encounter.id),
      label: encounter.name,
      sortLabel: encounter.name,
    }),
  )

  const updateParams = (next: { instance?: string; encounter?: string }) => {
    const params = new URLSearchParams()
    if (next.instance) params.set('instance', next.instance)
    if (next.encounter) params.set('encounter', next.encounter)
    setSearchParams(params, { replace: true })
  }

  const toolbar = (saveButton?: ReactNode) => (
    <div className='flex flex-wrap items-end gap-4'>
      <div className='flex flex-col gap-2 min-w-[240px]'>
        <div className='text-sm text-muted-foreground'>Instance</div>
        <Select
          value={instanceId}
          setValue={(value) => {
            const instance = instances.find(
              (entry) => String(entry.id) === value,
            )
            updateParams({
              instance: value,
              encounter: instance?.encounters[0]
                ? String(instance.encounters[0].id)
                : undefined,
            })
          }}
          options={instanceOptions}
          isSearchable
          placeholder='Select instance'
        />
      </div>
      <div className='flex flex-col gap-2 min-w-[240px]'>
        <div className='text-sm text-muted-foreground'>Encounter</div>
        <Select
          value={encounterId}
          setValue={(value) => {
            updateParams({
              instance: instanceId,
              encounter: value,
            })
          }}
          options={encounterOptions}
          disabled={!selectedInstance}
          placeholder='Select encounter'
        />
      </div>
      {saveButton}
    </div>
  )

  return (
    <Main title='Raid plan assignment defaults'>
      <div className='flex flex-col gap-6'>
        {selectedEncounter && selectedInstance ? (
          <AssignmentDefaultsWorkspace
            key={selectedEncounter.id}
            encounterId={selectedEncounter.id}
            gameVersion={selectedInstance.gameVersion as GameVersion}
            renderToolbar={toolbar}
          />
        ) : (
          toolbar()
        )}

        {isLoading ? (
          <div className='text-muted-foreground'>Loading…</div>
        ) : null}

        {error ? (
          <div className='text-destructive'>
            Failed to load assignment defaults. This page is development-only.
          </div>
        ) : null}
      </div>
    </Main>
  )
}

const AssignmentDefaultsWorkspace = ({
  encounterId,
  gameVersion,
  renderToolbar,
}: {
  encounterId: number
  gameVersion: GameVersion
  renderToolbar: (saveButton?: ReactNode) => ReactNode
}) => {
  const { data: assignments, isLoading } = useQuery({
    queryKey: ['devRaidPlanAssignmentDefaults', encounterId],
    queryFn: () => getDevRaidPlanAssignmentDefaults(encounterId),
  })
  const { data: customAbilities, isLoading: customAbilitiesLoading } = useQuery(
    {
      queryKey: ['devEncounterCustomAbilities', encounterId],
      queryFn: () => getDevEncounterCustomAbilities(encounterId),
    },
  )

  if (isLoading || customAbilitiesLoading || !assignments || !customAbilities) {
    return (
      <>
        {renderToolbar()}
        <div className='text-muted-foreground'>Loading…</div>
      </>
    )
  }

  return (
    <AssignmentDefaultsEditor
      encounterId={encounterId}
      gameVersion={gameVersion}
      initialAssignments={assignments}
      initialCustomAbilities={customAbilities.abilities}
      renderToolbar={renderToolbar}
    />
  )
}

const AssignmentDefaultsEditor = ({
  encounterId,
  gameVersion,
  initialAssignments,
  initialCustomAbilities,
  renderToolbar,
}: {
  encounterId: number
  gameVersion: GameVersion
  initialAssignments: DevRaidPlanAssignmentDefaults
  initialCustomAbilities: ConfiguredCustomAbility[]
  renderToolbar: (saveButton?: ReactNode) => ReactNode
}) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const timelineRootRef = useRef<HTMLDivElement>(null)
  const [, setAssignmentDialogOpen] = useState(false)
  const [timeline, setTimeline] = useState<RaidPlanTimeline>(() =>
    timelineFromAssignments(initialAssignments),
  )
  const [savedSnapshot, setSavedSnapshot] = useState(() =>
    JSON.stringify(
      assignmentsFromTimeline(timelineFromAssignments(initialAssignments)),
    ),
  )
  const [customAbilities, setCustomAbilities] = useState<
    ConfiguredCustomAbility[]
  >(() => initialCustomAbilities)
  const [savedCustomSnapshot, setSavedCustomSnapshot] = useState(() =>
    JSON.stringify(initialCustomAbilities),
  )
  const [draftSpellId, setDraftSpellId] = useState('')
  const [draftName, setDraftName] = useState('')
  const [draftColor, setDraftColor] = useState(DEFAULT_CUSTOM_ABILITY_COLOR)

  const updateTimeline = useCallback(
    (updater: (next: RaidPlanTimeline) => void) => {
      setTimeline((current) => {
        const next = cloneTimeline(current)
        updater(next)
        return next
      })
    },
    [],
  )

  const isDirty =
    JSON.stringify(assignmentsFromTimeline(timeline)) !== savedSnapshot
  const isCustomDirty = JSON.stringify(customAbilities) !== savedCustomSnapshot
  const hasUnsavedChanges = isDirty || isCustomDirty

  const saveMutation = useMutation({
    mutationFn: async () => {
      const [assignments, custom] = await Promise.all([
        isDirty
          ? saveDevRaidPlanAssignmentDefaults(
              encounterId,
              assignmentsFromTimeline(timeline),
            )
          : Promise.resolve(null),
        isCustomDirty
          ? saveDevEncounterCustomAbilities(encounterId, customAbilities)
          : Promise.resolve(null),
      ])
      return { assignments, custom }
    },
    onSuccess: (saved) => {
      if (saved.assignments) {
        queryClient.setQueryData(
          ['devRaidPlanAssignmentDefaults', encounterId],
          saved.assignments,
        )
        const next = timelineFromAssignments(saved.assignments)
        setTimeline(next)
        setSavedSnapshot(JSON.stringify(assignmentsFromTimeline(next)))
      }
      if (saved.custom) {
        queryClient.setQueryData(
          ['devEncounterCustomAbilities', encounterId],
          saved.custom,
        )
        setCustomAbilities(saved.custom.abilities)
        setSavedCustomSnapshot(JSON.stringify(saved.custom.abilities))
      }
      toast.success('Saved')
    },
    onError: (saveError) => {
      toast.error(
        saveError instanceof Error
          ? saveError.message
          : 'Failed to save assignment defaults',
      )
    },
  })

  const raidPlan = useMemo(
    () => buildFakeRaidPlan(encounterId, timeline),
    [encounterId, timeline],
  )

  const computed = useTeamPlanComputedData({
    raidPlan,
    timeline,
    selectedType: 'assignments',
    t,
    mergeAbilitiesAcrossDifficulties: true,
  })

  const updateCustomAbility = (
    index: number,
    next: Partial<ConfiguredCustomAbility>,
  ) => {
    setCustomAbilities((current) =>
      current.map((ability, abilityIndex) =>
        abilityIndex === index ? { ...ability, ...next } : ability,
      ),
    )
  }

  const addDraftCustomAbility = () => {
    const spellId = Number(draftSpellId)
    const name = draftName.trim()
    if (!Number.isInteger(spellId) || spellId <= 0 || !name) {
      toast.error('Spell id and name are required')
      return
    }
    if (customAbilities.some((ability) => ability.spell_id === spellId)) {
      toast.error('That spell id is already added')
      return
    }
    setCustomAbilities((current) => [
      ...current,
      { spell_id: spellId, name, color: draftColor },
    ])
    setDraftSpellId('')
    setDraftName('')
    setDraftColor(DEFAULT_CUSTOM_ABILITY_COLOR)
  }

  const timelineContextValue = useMemo(
    () => ({
      canEdit: true,
      owner: DEV_OWNER,
      basePath: '/dev/raid_plan_assignment_defaults',
      planListPathPrefix: '/dev/raid_plan_assignment_defaults',
      rosterMemberships: [],
      raidPlan,
      timeline,
      isInitializing: false,
      selectedType: 'assignments' as const,
      setSelectedType: () => undefined,
      zoom: null,
      setZoom: () => undefined,
      setDragFrameMax: () => undefined,
      computed,
      timelineRootRef,
      viewers: [],
      cursors: [],
      showRosterImport: false,
      cursorContext: 'tab:assignments',
      setAssignmentDialogOpen,
      sendCursor: () => undefined,
      updateTimeline,
      applyPlanFromSource: () => undefined,
      showAssignmentDifficulty: true,
      showAssignmentDescription: true,
      gameVersion,
      configuredCustomAbilities: customAbilities,
    }),
    [
      computed,
      customAbilities,
      gameVersion,
      raidPlan,
      timeline,
      updateTimeline,
    ],
  )

  return (
    <>
      {renderToolbar(
        <Button
          onClick={() => saveMutation.mutate()}
          disabled={!hasUnsavedChanges || saveMutation.isPending}
          loading={saveMutation.isPending}
        >
          Save
        </Button>,
      )}
      <div className='flex flex-col gap-4'>
        <Card className='space-y-3'>
          <div>
            <div className='font-medium'>Custom abilities</div>
            <div className='text-sm text-muted-foreground'>
              Add abilities that are not in the timeline. Users can pick them
              from New assignment. They start with zero casts.
            </div>
          </div>
          {customAbilities.length > 0 ? (
            <div className='space-y-2'>
              {customAbilities.map((ability, index) => (
                <div
                  key={`${ability.spell_id}-${index}`}
                  className='flex items-center gap-2'
                >
                  <SpellIcon
                    spellId={ability.spell_id}
                    className='w-6 h-6 rounded border border-border shrink-0'
                  />
                  <Input
                    type='text'
                    inputMode='numeric'
                    value={ability.spell_id || ''}
                    onChange={(event) => {
                      const nextSpellId = Number(event.target.value)
                      updateCustomAbility(index, {
                        spell_id: Number.isInteger(nextSpellId)
                          ? nextSpellId
                          : 0,
                      })
                    }}
                    placeholder='Spell ID'
                    className='w-28 h-8 shrink-0'
                  />
                  <Input
                    value={ability.name}
                    onChange={(event) =>
                      updateCustomAbility(index, { name: event.target.value })
                    }
                    placeholder='Name'
                    className='min-w-0 flex-1 h-8'
                  />
                  <input
                    type='color'
                    value={rgbToHex(ability.color)}
                    onChange={(event) =>
                      updateCustomAbility(index, {
                        color: hexToRgb(event.target.value),
                      })
                    }
                    className='h-8 w-10 shrink-0 cursor-pointer rounded border border-border bg-transparent p-0.5'
                    title='Default assignment color'
                  />
                  <Button
                    type='button'
                    variant='ghost'
                    size='icon'
                    onClick={() =>
                      setCustomAbilities((current) =>
                        current.filter(
                          (_, abilityIndex) => abilityIndex !== index,
                        ),
                      )
                    }
                  >
                    <Trash2 className='h-4 w-4' />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className='text-sm text-muted-foreground'>
              No custom abilities yet.
            </div>
          )}
          <div className='flex items-center gap-2'>
            <Input
              type='text'
              inputMode='numeric'
              value={draftSpellId}
              onChange={(event) => setDraftSpellId(event.target.value)}
              placeholder='Spell ID'
              className='w-28 h-8 shrink-0'
            />
            <Input
              value={draftName}
              onChange={(event) => setDraftName(event.target.value)}
              placeholder='Name'
              className='min-w-0 flex-1 h-8'
            />
            <input
              type='color'
              value={rgbToHex(draftColor)}
              onChange={(event) => setDraftColor(hexToRgb(event.target.value))}
              className='h-8 w-10 shrink-0 cursor-pointer rounded border border-border bg-transparent p-0.5'
              title='Default assignment color'
            />
            <Button
              type='button'
              variant='outline'
              size='sm'
              className='shrink-0'
              onClick={addDraftCustomAbility}
            >
              <Plus className='h-4 w-4' />
              Add
            </Button>
          </div>
        </Card>
        <div ref={timelineRootRef}>
          <TeamPlanTimelineProvider value={timelineContextValue}>
            <TeamPlanAssignments />
          </TeamPlanTimelineProvider>
        </div>
      </div>
    </>
  )
}

export default RaidPlanAssignmentDefaultsPage
